import type { Locker } from "shared/locker"
import type { LockerGrid, SstLocker } from "shared/sst"
import type { Sst2Locker, Sst2TemplateAssociation } from "shared/sst2"
import { orderedV2Modules } from "src/components/lockerModuleOrder"

/**
 * LOCKER-535 — the demo works on V1 and V2 lockers. A V1 locker (virtual ones
 * included) has no `sst2Locker`; its doors are `sstLocker.doors[]` keyed by
 * `door_id`. A V2 locker's doors are `sst2Locker.doors[]` keyed by `id`. The
 * demo map key is that id as a string, matching the backend validation.
 */
export type TemperatureDemoDoor = { id: string; label: string; number: number }

/** One cell of a module, in raw SST units. `door` is set only on a door cell with a known door. */
export type TemperatureDemoCell = {
  key: string
  x: number
  y: number
  width: number
  height: number
  isDoor: boolean
  door?: TemperatureDemoDoor
}

export type TemperatureDemoModule = {
  key: string
  width: number
  height: number
  cells: TemperatureDemoCell[]
}

const byNumber = (a: TemperatureDemoDoor, b: TemperatureDemoDoor) => a.number - b.number

/**
 * True when the locker renders on the V1 dashboard: `OsLockerPage` picks V2
 * only on `sstApiVersion === "v2"`, so a V1 row that still holds an
 * `sst2Locker` (e.g. after a rollback) is V1 too. A row with no `sstLocker`
 * has no V1 doors at all and stays on the V2 source.
 */
export const isTemperatureDemoV1 = (locker: Locker): boolean =>
  locker.sstApiVersion !== "v2" && !!locker.sstLocker

/** Every door of the locker — V1 (`sstLocker`) or V2 (`sst2Locker`) — in door order. */
export const temperatureDemoDoorList = (locker: Locker): TemperatureDemoDoor[] => {
  const sst2Locker = locker.sst2Locker as Sst2Locker | undefined
  if (!isTemperatureDemoV1(locker)) {
    return (sst2Locker?.doors ?? [])
      .map((d) => ({ id: String(d.id), label: d.door_label, number: d.door_number }))
      .sort(byNumber)
  }
  const sstLocker = locker.sstLocker as SstLocker | undefined
  return (sstLocker?.doors ?? [])
    .map((d) => ({
      id: String(d.door_id),
      label: d.door_label ?? String(d.door_number),
      number: d.door_number,
    }))
    .sort(byNumber)
}

const v2Modules = (locker: Locker, doors: TemperatureDemoDoor[]): TemperatureDemoModule[] => {
  const sst2Locker = locker.sst2Locker as Sst2Locker | undefined
  const byId = new Map(doors.map((d) => [d.id, d]))
  return orderedV2Modules<Sst2TemplateAssociation>(sst2Locker, locker.moduleMapping).map(
    ({ tmpl, originalIndex }) => {
      const moduleDoors = (sst2Locker?.doors ?? []).filter((d) => d.locker_group === originalIndex)
      return {
        key: String(tmpl.name || originalIndex),
        width: tmpl.template?.width ?? 400,
        height: tmpl.template?.height ?? 600,
        cells: (tmpl.template?.cells ?? []).map((cell) => {
          const isDoor = cell.cell_type.toUpperCase() === "DOOR"
          const sstDoor = isDoor ? moduleDoors.find((d) => d.cell_id === cell.id) : undefined
          return {
            key: String(cell.id),
            x: cell.x,
            y: cell.y,
            width: cell.width,
            height: cell.height,
            isDoor,
            door: sstDoor ? byId.get(String(sstDoor.id)) : undefined,
          }
        }),
      }
    },
  )
}

const v1Modules = (locker: Locker, doors: TemperatureDemoDoor[]): TemperatureDemoModule[] => {
  const grids = ((locker.sstLocker as SstLocker | undefined)?.locker_grid ?? []) as LockerGrid[]
  const byId = new Map(doors.map((d) => [d.id, d]))
  const indexed = grids.map((grid, i) => ({ grid, i }))
  // Same order as the V1 dashboard (`OsLockerDashboard.tsx`): any non-empty
  // `moduleMapping` lists grids by `sl2Idx`, skipping indexes it cannot find.
  const ordered = locker.moduleMapping?.length
    ? locker.moduleMapping.map((m) => indexed[m.sl2Idx]).filter((e) => e !== undefined)
    : indexed
  return ordered.map(({ grid, i }) => ({
    key: `${grid.name ?? ""}-${i}`,
    width: grid.width,
    height: grid.height,
    cells: (grid.cells_group ?? []).map((cell, j) => ({
      key: `${i}-${j}`,
      x: cell.x,
      y: cell.y,
      width: cell.width,
      height: cell.height,
      // V1 cell types are lowercase (`door`, `screen`); a door cell carries `door_id`.
      isDoor: !!cell.door_id || cell.cell_type?.toUpperCase() === "DOOR",
      door: cell.door_id ? byId.get(String(cell.door_id)) : undefined,
    })),
  }))
}

/** Module geometry for the mini map; empty when the locker carries none. */
export const temperatureDemoModules = (
  locker: Locker,
  doors: TemperatureDemoDoor[],
): TemperatureDemoModule[] =>
  isTemperatureDemoV1(locker) ? v1Modules(locker, doors) : v2Modules(locker, doors)
